import React, { useState, useCallback, useEffect } from "react";
import Axios from "axios";
import useAdvertisementAnalytics from "./useAdvertisementAnalytics";
import { AdvertisementMeta } from "./AdvertisementSeoMeta";

import {
  PageWrap, Grid, DecorBlob, DecorLeaf,
  HeroCol, Eyebrow, Title, OrnamentDivider, Subtitle,
  TrustList, TrustItem, TrustIconCircle, TrustTitle, TrustDesc,
  MobileTrustRow, MobileTrustItem, MobileTrustIconCircle, MobileTrustLabel,
  FormCol, Card, BadgeCircle, FormInner,
  FieldGroup, FieldLabel, InputShell, InputIcon, Input, FieldError,
  SubmitBtn, SpinnerRing, PrivacyNote, Note,
} from "./Advertisement.styles";

// ─── Constants ─────────────────────────────────────────────────────────────────

const API_URL =
  "https://us-central1-website-a1336.cloudfunctions.net/api/registerCallback";

const SOURCE = "Advertisement Form";

const INITIAL_FORM = {
  name:   "",
  mobile: "",
};

// ─── Icons ────────────────────────────────────────────────────────────────────

const UserIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.6" />
    <path d="M4.5 20c1-4 4-6 7.5-6s6.5 2 7.5 6" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 8.75a16 16 0 006.29 6.29l1.29-1.29a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7a2 2 0 011.72 2.02z" />
  </svg>
);

const ShieldIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 2.5l7.5 3.2v5.4c0 5-3.2 8.6-7.5 10.4-4.3-1.8-7.5-5.4-7.5-10.4V5.7L12 2.5z" />
    <path d="M9 12l2 2 4-4.5" />
  </svg>
);

const HeartIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20.5s-7.5-4.6-9.7-9.1C.7 8 2.3 4.7 5.6 4.1c2-.4 3.9.5 5 2.1a5 5 0 01.9 1.4 5 5 0 01.9-1.4c1.1-1.6 3-2.5 5-2.1 3.3.6 4.9 3.9 3.3 7.3-2.2 4.5-9.7 9.1-9.7 9.1z" />
  </svg>
);

const ArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

// ─── Trust copy — shared source for both the desktop list and the mobile row ──

const TRUST_ITEMS = [
  {
    id: "quick",
    Icon: PhoneIcon,
    title: "Quick Response",
    desc: "We'll connect you as soon as possible",
  },
  {
    id: "privacy",
    Icon: ShieldIcon,
    title: "100% Privacy",
    desc: "Your information is safe with us",
  },
  {
    id: "guidance",
    Icon: HeartIcon,
    title: "Personal Guidance",
    desc: "Get expert advice tailored to your needs",
  },
];

// ─── AdvertisementForm ────────────────────────────────────────────────────────
// Two-column layout on wider screens (hero copy + trust list on the left, the
// callback-request card on the right); collapses to a single stacked column
// with the trust items shown as a compact icon row below the card on mobile.

const AdvertisementForm = () => {
  const analytics = useAdvertisementAnalytics();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors,   setErrors]   = useState({});
  const [loading,  setLoading]  = useState(false);
  const [status,   setStatus]   = useState(null); // "success" | "error" | null

  // ── Form view on mount — mirrors ContactUsForm's trackFormView on mount ──
  useEffect(() => {
    analytics.trackFormView(window.location.href);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Validation ──
  const validate = useCallback((data) => {
    const e = {};
    if (!data.name.trim())                       e.name   = "Name is required";
    if (!/^\+?[\d\s-]{7,15}$/.test(data.mobile)) e.mobile = "Valid mobile number required";
    return e;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev)    => ({ ...prev, [name]: undefined }));
    setStatus(null);
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const errs = validate(formData);
    const hasErrors = Object.keys(errs).length > 0;

    analytics.trackSubmitAttempt(hasErrors);
    if (hasErrors) { setErrors(errs); return; }

    try {
      setLoading(true);
      setStatus(null);
      const payload = {
        name:   formData.name,
        mobile: formData.mobile,
        source: SOURCE,
      };
      const res = await Axios.post(API_URL, payload);
      if (res.data.code === "NP001") {
        analytics.trackSubmitSuccess();
        setStatus("success");
        setFormData(INITIAL_FORM);
      } else {
        analytics.trackSubmitError("server");
        setStatus("error");
      }
    } catch (err) {
      console.error("Advertisement form submission error:", err);
      analytics.trackSubmitError("network");
      setStatus("error");
    } finally {
      setLoading(false);
    }
  }, [formData, validate, analytics]);

  return (
    <PageWrap>
      {/* ── SEO — AdvertisementMeta mirrors ContactGeneralMeta pattern ── */}
      <AdvertisementMeta />

      <DecorBlob className="d1" aria-hidden="true" />
      <DecorBlob className="d2" aria-hidden="true" />
      <DecorLeaf viewBox="0 0 120 120" aria-hidden="true">
        <path
          d="M10 110C10 60 45 15 110 10c5 55-30 95-100 100z"
          fill="currentColor"
          opacity="0.5"
        />
      </DecorLeaf>

      <Grid>
        {/* ── Left: hero copy + trust list (desktop) ── */}
        <HeroCol>
          <Eyebrow>Free Consultation</Eyebrow>
          <Title>Book Your Free Consultation</Title>
          <OrnamentDivider viewBox="0 0 90 18" aria-hidden="true">
            <path
              d="M2 9c8-8 16 8 24 0s16-8 24 0 16 8 24 0 12-6 14-2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          </OrnamentDivider>
          <Subtitle>
            Share your details and one of our yoga experts will call you for a
            free, no-obligation consultation.
          </Subtitle>

          <TrustList>
            {TRUST_ITEMS.map(({ id, Icon, title, desc }) => (
              <TrustItem key={id}>
                <TrustIconCircle><Icon /></TrustIconCircle>
                <div>
                  <TrustTitle>{title}</TrustTitle>
                  <TrustDesc>{desc}</TrustDesc>
                </div>
              </TrustItem>
            ))}
          </TrustList>
        </HeroCol>

        {/* ── Right: callback request card ── */}
        <FormCol>
          <Card>
            <BadgeCircle aria-hidden="true"><PhoneIcon /></BadgeCircle>

            <FormInner onSubmit={handleSubmit} noValidate>
              <FieldGroup>
                <FieldLabel htmlFor="adName">Name *</FieldLabel>
                <InputShell>
                  <InputIcon aria-hidden="true"><UserIcon /></InputIcon>
                  <Input
                    id="adName" type="text" name="name"
                    placeholder="Shubham Gupta"
                    value={formData.name}
                    onChange={handleChange}
                    onFocus={() => analytics.trackFieldFocus("name")}
                    $error={!!errors.name}
                    aria-required="true"
                    aria-describedby={errors.name ? "adName-err" : undefined}
                  />
                </InputShell>
                {errors.name && (
                  <FieldError id="adName-err" role="alert">{errors.name}</FieldError>
                )}
              </FieldGroup>

              <FieldGroup>
                <FieldLabel htmlFor="adMobile">Mobile Number *</FieldLabel>
                <InputShell>
                  <InputIcon aria-hidden="true"><PhoneIcon /></InputIcon>
                  <Input
                    id="adMobile" type="tel" name="mobile"
                    placeholder="8810307173"
                    value={formData.mobile}
                    onChange={handleChange}
                    onFocus={() => analytics.trackFieldFocus("mobile")}
                    $error={!!errors.mobile}
                    aria-required="true"
                    aria-describedby={errors.mobile ? "adMobile-err" : undefined}
                  />
                </InputShell>
                {errors.mobile && (
                  <FieldError id="adMobile-err" role="alert">{errors.mobile}</FieldError>
                )}
              </FieldGroup>

              <SubmitBtn
                type="submit"
                disabled={loading}
                aria-busy={loading}
                aria-label="Book my free consultation"
              >
                {loading
                  ? <><SpinnerRing aria-hidden="true" /> Booking…</>
                  : <>Book Consultation <ArrowIcon /></>
                }
              </SubmitBtn>

              <PrivacyNote>
                <ShieldIcon aria-hidden="true" />
                We respect your privacy and will never share your details.
              </PrivacyNote>

              {status === "success" && (
                <Note $tone="success" role="status">
                  Thanks! Your free consultation is booked — we'll call you shortly.
                </Note>
              )}
              {status === "error" && (
                <Note $tone="error" role="alert">
                  Something went wrong. Please try again.
                </Note>
              )}
            </FormInner>
          </Card>
        </FormCol>
      </Grid>

      {/* ── Trust row (mobile only) ── */}
      <MobileTrustRow>
        {TRUST_ITEMS.map(({ id, Icon, title }) => (
          <MobileTrustItem key={id}>
            <MobileTrustIconCircle><Icon /></MobileTrustIconCircle>
            <MobileTrustLabel>{title}</MobileTrustLabel>
          </MobileTrustItem>
        ))}
      </MobileTrustRow>
    </PageWrap>
  );
};

AdvertisementForm.displayName = "AdvertisementForm";
export default React.memo(AdvertisementForm);